/**
 * The guided tour — a screen walked element by element, over its live data (ADR 9044).
 *
 * ## What a step points at
 *
 * A step names an ANCHOR, and the element carrying `data-tour="<anchor>"` is the one it
 * describes. The shared components carry theirs (the state strip, the run chooser, the export
 * button, the drill trail), so most screens tag only what is their own. `tools/doc-sections.mjs`
 * fails the census when a declared anchor is carried by no element in `src/`, so a step cannot
 * point at something that was renamed away.
 *
 * ## It never acts
 *
 * The tour highlights and explains. It does not press, open, approve or write anything, and it
 * does not hold the page: the panel is non-modal and the outline ignores the pointer. A step
 * about an approval button says what the button does; the planner is the one who presses it.
 *
 * ## It runs on the real page, never on example data
 *
 * Production carries no fixtures (ADR 9007, ADR 9013 — they are compiled out of the bundle), and
 * a tour must not bring them back. So when the element a step describes is not on the page —
 * no run chosen, figures not arrived — the step still reads, and says it is describing what
 * appears there once they have. It is never skipped and nothing is invented to fill it.
 *
 * ## How a tour is started
 *
 * From the page header's «Guided tour» button (the router mounts this), or from the guide in
 * Documentation, which navigates to the screen and leaves a request here for the router to take
 * once — in memory, not in the URL, because a `?tour=` the router could not remove again would
 * restart the tour on every reload of a shared link.
 */

import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { TourPanel, prefersReducedMotion } from '../primitives';

/** One step: the element it describes and the two catalogue keys it reads. */
export interface TourStep {
  /** The `data-tour` value of the element described — kebab-case. */
  readonly anchor: string;
  readonly titleKey: string;
  readonly bodyKey: string;
}

const ANCHOR = /^[a-z][a-z0-9-]*$/;

/** The first element on the page carrying the anchor, or null — pure over the document. */
export function findAnchor(anchor: string, root: ParentNode = document): HTMLElement | null {
  if (!ANCHOR.test(anchor)) return null;
  return root.querySelector<HTMLElement>(`[data-tour="${anchor}"]`);
}

interface Box {
  readonly top: number;
  readonly start: number;
  readonly width: number;
  readonly height: number;
  /** Where the element's middle sits, as a share of the window's height. */
  readonly middle: number;
}

function boxOf(element: HTMLElement): Box {
  const rect = element.getBoundingClientRect();
  const rtl = document.documentElement.dir === 'rtl';
  const height = window.innerHeight || 1;
  return {
    top: rect.top,
    // Logical: the distance from the inline-start edge, whichever side that is.
    start: rtl ? window.innerWidth - rect.right : rect.left,
    width: rect.width,
    height: rect.height,
    middle: (rect.top + rect.height / 2) / height,
  };
}

function sameBox(a: Box | null, b: Box | null): boolean {
  return a === b || (a !== null && b !== null
    && a.top === b.top && a.start === b.start && a.width === b.width && a.height === b.height);
}

/**
 * The element a step names and where it is, followed as the page changes under it. Polled
 * rather than observed: the element may not exist yet (its read is in flight) and appear later,
 * which no observer on an element can report, and one query a few times a second is nothing.
 */
function useAnchor(anchor: string): { readonly element: HTMLElement | null; readonly box: Box | null } {
  const [element, setElement] = useState<HTMLElement | null>(() => findAnchor(anchor));
  const [box, setBox] = useState<Box | null>(null);
  useEffect(() => {
    const update = (): void => {
      const found = findAnchor(anchor);
      setElement((current) => (current === found ? current : found));
      const next = found === null ? null : boxOf(found);
      setBox((current) => (sameBox(current, next) ? current : next));
    };
    update();
    const timer = window.setInterval(update, 400);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchor]);
  return { element, box };
}

/** The anchor of the page's one state strip (`page-status.tsx`). */
export const PAGE_STATUS_ANCHOR = 'page-status';

/**
 * The step a tour opens on when the page is showing its state strip — an error, no permission,
 * nothing published, nothing arrived — and the screen's guide has no step about it. Without it a
 * page that could not read its figures walks the planner through steps that each say "not showing
 * right now", and nothing explains the red strip above them.
 */
export const PAGE_STATUS_STEP: TourStep = {
  anchor: PAGE_STATUS_ANCHOR,
  titleKey: 'tour.pageStatus.title',
  bodyKey: 'tour.pageStatus.body',
};

/** Pure: the steps a tour walks, given whether the page shows its state strip when it opens. */
export function tourSteps(steps: readonly TourStep[], stripShowing: boolean): readonly TourStep[] {
  if (!stripShowing || steps.some((step) => step.anchor === PAGE_STATUS_ANCHOR)) return steps;
  return [PAGE_STATUS_STEP, ...steps];
}

export interface TourProps {
  readonly steps: readonly TourStep[];
  readonly onClose: () => void;
}

export function Tour({ steps: declared, onClose }: TourProps): React.JSX.Element | null {
  const [index, setIndex] = useState(0);
  // Whether the page shows its state strip. Looked for after mount and again while the planner is
  // still on the first step — a strip usually appears once a read has failed, after the tour
  // opened — and never after: adding a step then would renumber steps already read.
  const [stripShowing, setStripShowing] = useState(false);
  useEffect(() => {
    if (stripShowing || index !== 0) return undefined;
    const look = (): void => {
      if (findAnchor(PAGE_STATUS_ANCHOR) !== null) setStripShowing(true);
    };
    look();
    const timer = window.setInterval(look, 400);
    return () => window.clearInterval(timer);
  }, [stripShowing, index]);
  const steps = useMemo(() => tourSteps(declared, stripShowing), [declared, stripShowing]);
  const step = steps[Math.min(index, steps.length - 1)];
  const { element, box } = useAnchor(step?.anchor ?? '');

  // Bring the element into view once per step — not on every poll, which would fight a planner
  // scrolling away to read something else.
  useEffect(() => {
    if (element === null || typeof element.scrollIntoView !== 'function') return;
    element.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }, [element]);

  // Escape ends the tour from anywhere on the page, not only from inside the panel.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (step === undefined || typeof document === 'undefined') return null;

  return createPortal(
    <>
      {box !== null && (
        <div
          aria-hidden
          data-tour-highlight={step.anchor}
          className="outline-focus-ring z-modal pointer-events-none fixed rounded-control outline outline-2 outline-offset-4"
          style={{ insetBlockStart: box.top, insetInlineStart: box.start, inlineSize: box.width, blockSize: box.height }}
        />
      )}
      <TourPanel
        titleKey={step.titleKey}
        bodyKey={step.bodyKey}
        index={index}
        count={steps.length}
        absent={element === null}
        // Away from what it describes: an element in the lower half puts the panel at the top.
        dock={box !== null && box.middle > 0.5 ? 'block-start' : 'block-end'}
        onBack={() => setIndex((current) => Math.max(0, current - 1))}
        onNext={() => setIndex((current) => Math.min(steps.length - 1, current + 1))}
        onClose={onClose}
      />
    </>,
    document.body,
  );
}

// ── the request Documentation leaves for the router ─────────────────────────

/** How long a request waits for its screen: a navigation, not a later visit. */
const REQUEST_LIFETIME_MS = 5000;

let pending: { readonly path: string; readonly at: number } | null = null;

/** Documentation asks for the tour of the screen it is about to open. */
export function requestTour(path: string): void {
  pending = { path, at: Date.now() };
}

/**
 * The router takes the request once, for the path it is rendering. A request for another path is
 * left for that path's render; one older than a navigation takes is dropped, so a screen visited
 * later does not open on a tour nobody asked for then.
 */
export function takeTourRequest(path: string): boolean {
  if (pending === null) return false;
  if (Date.now() - pending.at > REQUEST_LIFETIME_MS) {
    pending = null;
    return false;
  }
  if (pending.path !== path) return false;
  pending = null;
  return true;
}
